import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import type { MemberDetailResponse, WorkspaceDetailResponse } from "@/lib/api/generated";
import { getWorkspaceDetailServer, updateWorkspaceServer } from "@/server/workspaces";
import { getCurrentMemberServer } from "@/server/workspace-members";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractApiErrorMessage } from "@/lib/api/error";
import { WorkspaceLogoUploader } from "@/components/settings/workspace-logo-uploader";
import { TimezoneSelector } from "@/components/workspace/timezone-selector";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface WorkspaceFormData {
  display_name: string;
  timezone: string;
  language: string;
}

interface LoaderData {
  workspace: WorkspaceDetailResponse;
  currentMember: MemberDetailResponse;
}

export const Route = createFileRoute("/workspace/$workspaceSlug/settings/workspace/general")({
  loader: async ({ context, parentMatchPromise }): Promise<LoaderData> => {
    const workspaceId = await getWorkspaceIdWithFallback(
      context,
      "settings.workspace.general",
      parentMatchPromise,
    );

    const [workspace, currentMember] = await Promise.all([
      getWorkspaceDetailServer({ data: { workspaceId } }),
      getCurrentMemberServer({ data: { workspaceId } }),
    ]);

    return { workspace, currentMember };
  },
  component: GeneralPage,
});

function GeneralPage() {
  const { t } = useTranslation(["settings"]);
  const { workspace, currentMember } = Route.useLoaderData();
  const router = useRouter();

  // 检查当前用户是否是 workspace owner
  const isOwner = currentMember.role === "workspace_owner";

  const currentWorkspaceTimezone = (
    workspace as WorkspaceDetailResponse & {
      timezone?: string;
    }
  ).timezone;

  const workspaceSchema = useMemo(
    () =>
      z.object({
        display_name: z
          .string()
          .min(1, t("workspace.generalPage.name.required"))
          .max(100, t("workspace.generalPage.name.maxLength"))
          .trim(),
        timezone: z.string().min(1, t("workspace.generalPage.timezone.required")),
        language: z.string().min(1, t("workspace.generalPage.language.required")),
      }),
    [t],
  );

  const form = useForm<WorkspaceFormData>({
    resolver: zodResolver(workspaceSchema),
    defaultValues: {
      display_name: workspace.display_name,
      timezone: currentWorkspaceTimezone || "UTC",
      language: (workspace as WorkspaceDetailResponse & { language?: string }).language || "en",
    },
  });

  useEffect(() => {
    if (!currentWorkspaceTimezone) {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTimezone && browserTimezone !== form.getValues("timezone")) {
        form.setValue("timezone", browserTimezone, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [currentWorkspaceTimezone, form]);

  const onSubmit = async (data: WorkspaceFormData): Promise<void> => {
    try {
      await updateWorkspaceServer({
        data: {
          workspaceId: workspace.id,
          payload: {
            display_name: data.display_name,
            timezone: data.timezone,
            language: data.language,
          },
        },
      });

      toast.success(t("workspace.generalPage.success"));

      // Reload the page data to reflect changes
      router.invalidate();
    } catch (error) {
      const message = extractApiErrorMessage(error, t("workspace.generalPage.error"));
      toast.error(message);
    }
  };

  const isDirty = form.formState.isDirty;
  const isSubmitting = form.formState.isSubmitting;

  return (
    <div className="p-6">
      <div className="max-w-2xl">
        <h2 className="text-[16px] font-semibold text-gray-900 mb-6">
          {t("workspace.generalPage.title")}
        </h2>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Logo Upload */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              {t("workspace.generalPage.logo.label")}
            </Label>
            <WorkspaceLogoUploader
              workspaceId={workspace.id}
              currentLogoUrl={workspace.logo_url}
              onUploadSuccess={() => router.invalidate()}
              disabled={!isOwner}
            />
          </div>

          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="display_name" className="text-sm font-medium text-gray-700">
              {t("workspace.generalPage.name.label")}
            </Label>
            <Input
              id="display_name"
              {...form.register("display_name")}
              placeholder={t("workspace.generalPage.name.placeholder")}
              className="max-w-md"
              disabled={!isOwner}
            />
            {form.formState.errors.display_name && (
              <p className="text-sm text-red-600">{form.formState.errors.display_name.message}</p>
            )}
          </div>

          {/* URL Field (Read-only) */}
          <div className="space-y-2">
            <Label htmlFor="slug" className="text-sm font-medium text-gray-700">
              {t("workspace.generalPage.url.label")}
            </Label>
            <div className="flex items-center max-w-md">
              <span className="text-sm text-gray-500 mr-1">
                {t("workspace.generalPage.url.prefix")}
              </span>
              <Input
                id="slug"
                value={workspace.slug}
                disabled
                className="bg-gray-50 text-gray-500 cursor-not-allowed flex-1"
              />
            </div>
            <p className="text-xs text-gray-500">{t("workspace.generalPage.url.hint")}</p>
          </div>

          <div className="space-y-2 max-w-md">
            <Label className="text-sm font-medium text-gray-700 block">
              {t("workspace.generalPage.language.label")}
            </Label>
            <Select
              value={form.watch("language")}
              onValueChange={(value) => form.setValue("language", value, { shouldDirty: true })}
              disabled={!isOwner}
            >
              <SelectTrigger className="w-full h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">{t("workspace.generalPage.languages.en")}</SelectItem>
                <SelectItem value="zh">{t("workspace.generalPage.languages.zh")}</SelectItem>
              </SelectContent>
            </Select>
            {form.formState.errors.language && (
              <p className="text-sm text-red-600">{form.formState.errors.language.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <TimezoneSelector
              id="timezone"
              label={t("workspace.generalPage.timezone.label")}
              value={form.watch("timezone")}
              onChange={(value) => form.setValue("timezone", value, { shouldDirty: true })}
              disabled={!isOwner}
              className="max-w-md"
              triggerClassName="w-full h-11"
            />
            {form.formState.errors.timezone && (
              <p className="text-sm text-red-600">{form.formState.errors.timezone.message}</p>
            )}
          </div>

          {/* Save Button - Only visible to owners */}
          {isOwner && (
            <div className="pt-4">
              <Button
                type="submit"
                disabled={(!isDirty && !!currentWorkspaceTimezone) || isSubmitting}
              >
                {isSubmitting ? t("workspace.generalPage.saving") : t("workspace.generalPage.save")}
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
